/**
 * AimPad — a "trackpad" that steers the total-station scope.
 *
 * Behaviour
 * ---------
 *   • Tap-and-drag anywhere on the pad → the scope slews continuously
 *     in that direction (joystick style — same as Leica Automat).
 *     Distance from centre = angular speed. Release = stop.
 *   • Uses MOT_SetVelocity under the hood (non-blocking) so drag
 *     doesn't queue up 5000 ms make_positioning() timeouts.
 *   • Buttons:
 *       [ SET AS HOME ]   Records the scope's *current* Hz/V as the
 *                          session's fall-back point. A tiny marker
 *                          then floats on the pad.
 *       [ RETURN HOME ]   Slews to the saved home now.
 *   • Auto-return: when a prism is active AND the ATR flag flips
 *     locked→lost, the pad automatically calls returnHome() so the
 *     scope goes back to a known good aim.
 *
 * Props
 *   tool       — "ovality" | "verticality" | "level"
 *   sessionId  — active session id
 *   compact    — smaller pad (used when the rail is tight)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { House, Target as TargetIcon, ArrowUUpLeft, Sun } from "@phosphor-icons/react";
import { station } from "../lib/api";

// Peak joystick speed when the finger is pegged at the pad edge.
// ~17°/s Hz and V — matches Leica Automat's medium-fast joystick.
// Tweak here if it feels too sluggish or too jumpy on your prisms.
const MAX_VHZ_RAD_S = 0.30;   // ≈ 17 °/s
const MAX_VV_RAD_S = 0.30;    // ≈ 17 °/s
// Dead-zone around centre so a still finger doesn't dribble the scope.
const DEADZONE = 0.08;
// Velocity update cadence — same as the demo tracer (~25 Hz).
const SLEW_INTERVAL_MS = 50;

export const AimPad = ({ tool, sessionId, compact = false }) => {
  const padRef = useRef(null);
  const [dragXY, setDragXY] = useState(null);          // {x,y} in pad-frac
  const [home, setHome] = useState(null);              // {hz, v} radians
  const [busy, setBusy] = useState(false);

  // Track ATR lock state so we can auto-return when a prism drops lock.
  const atrLockedRef = useRef(false);
  const isPrismRef = useRef(false);
  const [laserOn, setLaserOn] = useState(false);
  const [tsTracking, setTsTracking] = useState(false);
  // Cached current aim (Hz/V) so setAsHome/returnHome can operate
  // against the freshest reading if the direct state() call fails.
  const currentAimRef = useRef({ hz: 0, v: Math.PI / 2 });

  // Fetch home for this session
  useEffect(() => {
    if (!sessionId) return;
    station.homeGet(tool, sessionId).then((r) => {
      if (r.hz != null && r.v != null) setHome({ hz: r.hz, v: r.v });
      else setHome(null);
    }).catch(() => undefined);
  }, [tool, sessionId]);

  // Poll TS state — updates the cached aim + surfaces TS-side
  // tracking so the AimPad can show its badge and disable slew.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await station.state();
        if (cancelled) return;
        if (Number.isFinite(s.hz) && Number.isFinite(s.v)) {
          currentAimRef.current = { hz: s.hz, v: s.v };
        }
        setLaserOn(!!s.laser_on);
        setTsTracking(!!s.ts_tracking);
        atrLockedRef.current = !!s.atr_locked;
        isPrismRef.current = (s.target_type === 0);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 800);
    return () => { cancelled = true; clearInterval(id); };
  }, [tool, sessionId]);

  // ---- Continuous velocity slew ------------------------------------
  // Pad frac (x, y in [-1..1]) → (vhz, vv) rad/s. A small dead-zone
  // in the middle prevents the scope drifting when the finger is
  // hovering near centre. A soft-knee curve (x^1.5) keeps small
  // corrections fine while still reaching MAX_V near the edge.

  // Latest requested velocity, refreshed every pointer move.
  const targetVelRef = useRef({ vhz: 0, vv: 0 });
  const inFlightRef = useRef(false);
  const tickerRef = useRef(null);

  const computeVelocity = (x, y) => {
    const cx = Math.max(-1, Math.min(1, x));
    const cy = Math.max(-1, Math.min(1, y));
    const mag = Math.hypot(cx, cy);
    if (mag < DEADZONE) return { vhz: 0, vv: 0 };
    // Rescale magnitude past the dead-zone to still reach 1.0 at edge.
    const scaled = (mag - DEADZONE) / (1 - DEADZONE);
    const curved = Math.pow(scaled, 1.5);              // soft knee
    const ux = cx / mag;
    const uy = cy / mag;
    return {
      vhz: ux * curved * MAX_VHZ_RAD_S,
      // GeoCOM V is measured from zenith: +dV = tilt DOWN. Finger at
      // top of pad (uy < 0) → user wants scope UP → +vv is wrong,
      // so we keep the sign of uy (top-negative = negative vv = -V = up).
      vv: uy * curved * MAX_VV_RAD_S,
    };
  };

  // Ship the current target velocity to the bridge. Guarded so we
  // never have two in flight at once — otherwise a slow WAN round-trip
  // can stack up commands and eventually time out.
  const shipVelocity = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const { vhz, vv } = targetVelRef.current;
    try {
      await station.slew(vhz, vv);
    } catch { /* silent — next tick will retry */ }
    finally { inFlightRef.current = false; }
  }, []);

  const startTicker = useCallback(() => {
    if (tickerRef.current) return;
    tickerRef.current = setInterval(shipVelocity, SLEW_INTERVAL_MS);
  }, [shipVelocity]);

  const stopTicker = useCallback(() => {
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
  }, []);

  // Cleanup on unmount — make sure the scope isn't left crawling if
  // the user navigates away mid-drag.
  //
  // CRITICAL: skip the stop-slew call if the instrument is currently
  // locked onto a prism. `station.slew(0, 0)` on the bridge calls
  // `release_motors()` which flips the motor controller from LOCK to
  // MANUPOS mode — destroying the tracking lock the surveyor just
  // engaged. This was the "Set Home & Lock Prism engages then
  // instantly loses lock" bug (proven by the /api/debug/audit
  // trail): AimPad is rendered only in the "home" phase, and the
  // moment powerSearch succeeds phase transitions to "corner",
  // AimPad unmounts and its old cleanup fired slew(0,0) which
  // killed the fresh lock inside 20 ms.
  useEffect(() => () => {
    stopTicker();
    if (atrLockedRef.current) return;   // tracking active — leave the servo alone
    station.slew(0, 0).catch(() => undefined);
  }, [stopTicker]);

  // ---- Pointer handling --------------------------------------------

  const posOf = (e) => {
    const rect = padRef.current.getBoundingClientRect();
    // Pointer Events expose clientX/Y directly (unified for mouse+touch).
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    const x = ((cx - rect.left) / rect.width) * 2 - 1;
    const y = ((cy - rect.top) / rect.height) * 2 - 1;
    return { x, y };
  };

  // Track the last pad-fraction touched so a fresh pointerDown can
  // re-fire an initial velocity command immediately.
  const lastFracRef = useRef(null);

  const onPointerDown = (e) => {
    // If the TS is actively tracking a prism (firmware servo-lock),
    // sending our own MOT_SetVelocity would tear the head out from
    // under the tracking routine. Better to disable drag entirely
    // and prompt the surveyor to stop tracking on the instrument
    // first.
    if (tsTracking) {
      toast("TS is tracking a prism — stop tracking on the instrument to nudge");
      return;
    }
    // Pointer Events unify mouse + touch and are the only reliable
    // path on Android Chrome inside a scrollable ancestor. Capture the
    // pointer so subsequent moves keep coming to *this* element even
    // if the finger drifts outside its bounds.
    e.preventDefault();
    try { e.target.setPointerCapture(e.pointerId); } catch { /* ok */ }
    const p = posOf(e);
    setDragXY(p);
    lastFracRef.current = p;
    targetVelRef.current = computeVelocity(p.x, p.y);
    // Fire the first velocity command immediately, then let the
    // ticker keep it fresh until release.
    shipVelocity();
    startTicker();
  };
  const onPointerMove = (e) => {
    if (!dragXY) return;
    e.preventDefault();
    const p = posOf(e);
    setDragXY(p);
    lastFracRef.current = p;
    targetVelRef.current = computeVelocity(p.x, p.y);
  };
  const onPointerUp = (e) => {
    try { e.target.releasePointerCapture(e.pointerId); } catch { /* ok */ }
    setDragXY(null);
    lastFracRef.current = null;
    targetVelRef.current = { vhz: 0, vv: 0 };
    stopTicker();
    // Guarantee the (0,0) stop is the LAST slew message the bridge
    // sees for this gesture — otherwise a still-in-flight nonzero
    // velocity can land after our stop and the scope keeps crawling.
    (async () => {
      while (inFlightRef.current) {
        await new Promise((r) => setTimeout(r, 20));
      }
      try { await station.slew(0, 0); } catch { /* silent */ }
    })();
  };
  const onPointerCancel = onPointerUp;

  const setAsHome = async () => {
    setBusy(true);
    try {
      // Fresh live-read from the instrument — the polled state cache
      // is up to 250 ms stale which is a big enough error for Return
      // Home to overshoot after a drag. Fall back to state() if the
      // live endpoint isn't available (older bridge builds).
      let hz, v;
      try {
        const l = await station.liveAngles();
        if (Number.isFinite(l.hz) && Number.isFinite(l.v)) { hz = l.hz; v = l.v; }
      } catch { /* fall through */ }
      if (hz == null || v == null) {
        try {
          const s = await station.state();
          if (Number.isFinite(s.hz) && Number.isFinite(s.v)) { hz = s.hz; v = s.v; }
        } catch { /* fall through */ }
      }
      if (hz == null || v == null) {
        hz = currentAimRef.current.hz;
        v = currentAimRef.current.v;
      }
      setHome({ hz, v });
      // Persist to cloud in the background — best-effort, don't block.
      station.homeSet({ tool, session_id: sessionId, hz, v }).catch(() => undefined);
      toast.success("Home set to current aim");
    } catch {
      toast.error("Could not set home");
    } finally { setBusy(false); }
  };

  const returnHome = async () => {
    if (!home) { toast.error("No home set — tap SET AS HOME first"); return; }
    setBusy(true);
    try {
      // Drive the real TS via the bridge's absolute-turn endpoint.
      await station.turn({ hz: home.hz, v: home.v });
      // Keep the delta cache in sync so subsequent taps slew relative
      // to the freshly-returned aim.
      currentAimRef.current = { hz: home.hz, v: home.v };
      toast.success("Returning to home");
    } catch {
      toast.error("Return home failed");
    } finally { setBusy(false); }
  };

  const toggleLaser = async () => {
    const next = !laserOn;
    setLaserOn(next);  // optimistic
    try {
      await station.laser(next);
      toast[next ? "success" : "message"](`Red laser ${next ? "ON" : "off"}`);
    } catch {
      setLaserOn(!next);
      toast.error("Could not toggle laser");
    }
  };

  const size = compact ? "h-32" : "h-40";
  const padPct = dragXY
    ? { left: `${((dragXY.x + 1) / 2) * 100}%`, top: `${((dragXY.y + 1) / 2) * 100}%` }
    : null;

  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-3" data-testid="aim-pad">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
          Aim pad
        </span>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="aim-laser-toggle"
            onClick={toggleLaser}
            className={`flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[9px] uppercase tracking-widest transition-all ${
              laserOn
                ? "border-[#FF3B30] bg-[#FF3B30]/20 text-[#FF3B30] shadow-[0_0_8px_rgba(255,59,48,0.6)]"
                : "border-[#2A2E39] bg-[#14161A] text-[#8B939A] hover:border-[#FF3B30]/60 hover:text-[#FF3B30]"
            }`}
            title="Toggle the visible red laser dot on the TS"
          >
            <Sun size={10} weight={laserOn ? "fill" : "bold"} />
            Laser {laserOn ? "ON" : "off"}
          </button>
          {home && (
            <span className="flex items-center gap-1 rounded-sm border border-[#8DC63F]/60 bg-[#8DC63F]/10 px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#8DC63F]">
              <House size={10} weight="fill" /> Home armed
            </span>
          )}
          {tsTracking && (
            <span
              data-testid="aim-ts-tracking"
              className="flex items-center gap-1 rounded-sm border border-[#00E1C7] bg-[#00E1C7]/15 px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#00E1C7] animate-pulse"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#00E1C7]" />
              TS Tracking
            </span>
          )}
        </div>
      </div>

      {/* Pad — tap/drag to slew */}
      <div
        ref={padRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerUp}
        className={`relative ${size} cursor-crosshair select-none overflow-hidden rounded-md border-2 border-[#2A2E39] bg-[radial-gradient(circle,#1A1D24_0%,#0A0B0E_100%)]`}
        style={{ touchAction: "none" }}
      >
        {/* Cross-hair */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-[#2A2E39]" />
          <div className="absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-[#2A2E39]" />
          <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#3A3F49]" />
        </div>

        {/* Home marker (approximate centre — we don't know current aim
            relative to home in real time, so just show that it exists) */}
        {home && (
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#8DC63F]/60 bg-[#8DC63F]/10"
            title="Home aim armed"
          >
            <House size={10} weight="fill" className="text-[#8DC63F]" />
          </div>
        )}

        {/* Live drag pip */}
        {padPct && (
          <div
            className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#009E8E] bg-[#009E8E]/40"
            style={padPct}
          />
        )}

        {/* Corner hint */}
        <div className="pointer-events-none absolute bottom-1 right-2 text-[8px] uppercase tracking-widest text-[#4A5054]">
          tap · drag
        </div>
      </div>

      {/* Actions */}
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          data-testid="aim-set-home"
          disabled={busy}
          onClick={setAsHome}
          className="flex items-center justify-center gap-1 rounded-md border border-[#8DC63F]/60 bg-[#8DC63F]/10 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#8DC63F] hover:bg-[#8DC63F]/20 disabled:opacity-40"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          <TargetIcon size={11} weight="bold" /> Set as home
        </button>
        <button
          data-testid="aim-return-home"
          disabled={busy || !home}
          onClick={returnHome}
          className="flex items-center justify-center gap-1 rounded-md border border-[#009E8E]/60 bg-[#009E8E]/10 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20 disabled:opacity-40"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          <ArrowUUpLeft size={11} weight="bold" /> Return home
        </button>
      </div>

      <div className="mt-1 text-center text-[9px] uppercase tracking-widest text-[#4A5054]">
        {home ? "Auto-returns if prism lock is lost" : "Home not set"}
      </div>
    </div>
  );
};

export default AimPad;
